/**
 * User mention — trigger `&` urmat de query. Suggestion peste membrii
 * tenantului, scoring startsWith > includes pe email. La select inserează
 * UserMentionNode cu attrs {userId, email, role}.
 *
 * Alegem `&` ca trigger: nu clash cu `@` (credentiale) și nu apare
 * natural în scrierea curentă de notițe.
 */
import { Extension, type Editor } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';
import { useTranslation } from 'react-i18next';

import { makeSuggestionRenderer } from '@/features/notes/suggestionRenderer';
import { tenantsApi, type TenantMember } from '@/api/tenants';

const USER_MENU_KEY = new PluginKey('cerbes-user-menu');

export function createUserMenu(tenantId: string): Extension {
  let cache: TenantMember[] | null = null;
  let cachedAt = 0;

  const getMembers = async (): Promise<TenantMember[]> => {
    const now = Date.now();
    if (cache && now - cachedAt < 30_000) return cache;
    if (!tenantId) return [];
    try {
      const list = await tenantsApi.listMembers(tenantId);
      cache = list;
      cachedAt = now;
      return list;
    } catch {
      return [];
    }
  };

  return Extension.create({
    name: 'userMenu',
    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          pluginKey: USER_MENU_KEY,
          char: '&',
          startOfLine: false,
          allowSpaces: false,
          items: async ({ query }: { query: string }) => {
            const all = await getMembers();
            const q = query.trim().toLowerCase();
            if (!q) return all.slice(0, 10);
            const scored = all
              .map((m) => {
                const e = m.email.toLowerCase();
                const r = m.role.toLowerCase();
                let s = -1;
                if (e.startsWith(q)) s = 3;
                else if (e.includes(q)) s = 2;
                else if (r.includes(q)) s = 1;
                return { m, s };
              })
              .filter((x) => x.s >= 0)
              .sort((a, b) => b.s - a.s);
            return scored.slice(0, 10).map((x) => x.m);
          },
          command: ({
            editor,
            range,
            props,
          }: {
            editor: Editor;
            range: { from: number; to: number };
            props: TenantMember;
          }) => {
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent([
                {
                  type: 'userMention',
                  attrs: {
                    userId: props.user_id,
                    email: props.email,
                    role: props.role,
                  },
                },
                { type: 'text', text: ' ' },
              ])
              .run();
          },
          render: makeSuggestionRenderer(UserList),
        } as SuggestionOptions),
      ];
    },
  });
}

function UserList(props: {
  items: TenantMember[];
  command: (item: TenantMember) => void;
  selectedIndex: number;
}) {
  const { items, command, selectedIndex } = props;
  const { t } = useTranslation();
  if (items.length === 0) {
    return (
      <div
        className="rounded-md shadow-lg px-3 py-2 text-xs"
        style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-strong)',
          color: 'var(--text-muted)',
        }}
      >
        {t('notes_blocks.user_menu.empty')}
      </div>
    );
  }
  return (
    <div
      className="rounded-md shadow-lg py-1 max-h-64 overflow-auto min-w-[260px]"
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-strong)',
      }}
    >
      {items.map((item, idx) => {
        const active = idx === selectedIndex;
        const initials = item.email.slice(0, 2).toUpperCase();
        return (
          <button
            key={item.user_id}
            type="button"
            onClick={() => command(item)}
            className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors"
            style={{
              background: active ? 'var(--accent-bg)' : 'transparent',
              color: active ? 'var(--accent)' : 'var(--text)',
            }}
            onMouseEnter={(e) => {
              if (!active) e.currentTarget.style.background = 'var(--bg-hover)';
            }}
            onMouseLeave={(e) => {
              if (!active) e.currentTarget.style.background = 'transparent';
            }}
          >
            <span
              className="inline-grid place-items-center w-6 h-6 rounded-full text-[10px] font-semibold flex-shrink-0"
              style={{
                background: active ? 'var(--accent)' : 'var(--bg-surface)',
                color: active ? 'var(--bg)' : 'var(--text-muted)',
                border: active ? 'none' : '1px solid var(--border)',
              }}
            >
              {initials}
            </span>
            <span className="flex-1 min-w-0">
              <div className="font-mono truncate">{item.email}</div>
              <div className="text-[10px]" style={{ color: active ? 'var(--accent)' : 'var(--text-muted)' }}>
                {item.role}
              </div>
            </span>
          </button>
        );
      })}
    </div>
  );
}
